import { styled } from "@mui/material/styles";

import Typography, { TypographyProps } from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { usePageContext } from "@portal/app/_components/context/PageContext";
import { CoachImage } from "@portal/app/_components/ui-elements/Coach";
import cheerfulShirt from "@portal/app/_components/resources/images/coach/cheerful-shirt.svg";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import Link from "next/link";
import { getUrls } from "@portal/app/_common/lib/urls";

export const DreamSearchFormFunnelBanner = () => {
  const { isMobile, clientLocation } = usePageContext();
  const analytics = useMixpanel();
  const urls = getUrls();

  return (
    <Container data-testid="dreamsearch-form-funnel-banner">
      <CTAContainer>
        <TextContainer>
          {/* CTA copy, not a section heading — keeps the new_h3 styling, drops the <h3>. */}
          <HeaderTypography variant="new_h3" component="p">
            Need help picking a program?
          </HeaderTypography>
          <SubheaderTypography as="span">Take our 2 minute quiz</SubheaderTypography>
        </TextContainer>
        <ActionContainer>
          <Link href={urls.FormFunnelHome()}>
            <StyledStartButton
              data-testid="form-funnel-banner-start-button"
              onClick={() =>
                analytics.track("Dream Search - Clicked Find My Match", {
                  zip: clientLocation.zip,
                })
              }
            >
              Start
            </StyledStartButton>
          </Link>
        </ActionContainer>
      </CTAContainer>
      <ImageContainer>
        <CoachContainer>
          <CoachImage size={isMobile ? 100 : 115} src={cheerfulShirt.src} />
        </CoachContainer>
      </ImageContainer>
    </Container>
  );
};

const Container = styled("div")`
  border-radius: 1rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem;
  background: ${({ theme }) => theme.palette.primary.main};

  @container (width > 500px) {
    flex-direction: row-reverse;
  }
`;

const ImageContainer = styled("div")`
  position: relative;
  align-self: stretch;
  min-width: 5rem;

  ${({ theme }) => theme.breakpoints.up("md")} {
    min-width: 7rem;
  }

  @container (width < 500px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

const CoachContainer = styled("div")`
  @container (width > 500px) {
    position: absolute;
    bottom: 0;
    right: 0;
    margin-top: -1.25rem;
  }
`;

const CTAContainer = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;

  @container (width > 500px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const TextContainer = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
`;

const HeaderTypography = styled(Typography)<TypographyProps>`
  color: white;
  margin: 0;
`;

const SubheaderTypography = styled(Typography)`
  color: white;
  margin: 0;
  font-size: 1rem;
`;

const ActionContainer = styled("div")`
  display: flex;
  flex-direction: row;
`;

const StyledStartButton = styled(Button)`
  background-color: white;
  color: ${({ theme }) => theme.palette.primary.main};

  &:hover {
    background-color: ${({ theme }) => theme.palette.dream.grayLight};
    color: ${({ theme }) => theme.palette.primary.main};
  }

  text-wrap: nowrap;
`;
